import React from "react"
import { useHistory } from "react-router-dom"
import {
    Avatar,
    Box,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { WhatsApp } from "@material-ui/icons"
import { makeStyles } from "@material-ui/core/styles"
import { useClientesFornecedorContext } from "../context/useClientesFornecedorContext"
import type { ClienteItem } from "../../../services/clientesFornecedor"

const useStyles = makeStyles(theme => ({
    card: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    cardContent: {
        padding: theme.spacing(2),
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        cursor: "pointer",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    emailCell: {
        maxWidth: 160,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    wppBtn: {
        color: "#4caf50",
    },
    rodape: {
        padding: theme.spacing(1.5, 2),
        color: "#7f8c8d",
        fontSize: "0.85rem",
    },
    semResultado: {
        textAlign: "center",
        padding: theme.spacing(4),
        color: "#7f8c8d",
    },
}))

function formatarData(ts: number | null): string {
    if (!ts) return "-"
    return new Date(ts).toLocaleDateString("pt-BR")
}

function linkWhatsapp(cliente: ClienteItem): string {
    if (!cliente.telefone) return "#"
    let tel = ("55" + cliente.telefone)
        .replace(/\s/g, "")
        .replace(/-/g, "")
        .replace(/\(/g, "")
        .replace(/\)/g, "")
        .replace(/\./g, "")
        .replace(/\+/g, "")
    const texto = encodeURIComponent(`Olá ${cliente.nome || ""}! `)
    return `https://api.whatsapp.com/send?phone=${tel}&text=${texto}`
}

export function TabelaClientes() {
    const classes = useStyles()
    const history = useHistory()
    const { dados, buscar } = useClientesFornecedorContext()

    const handleMudarPagina = (_: unknown, novaPagina: number) => {
        buscar(dados.filtro, novaPagina)
    }

    return (
        <Paper className={classes.card}>
            <Box className={classes.cardHeader}>
                <Typography className={classes.cardHeaderTitle}>Clientes</Typography>
            </Box>

            <Box className={classes.cardContent}>
                <TableContainer style={{ maxHeight: 500, overflowX: "auto" }}>
                    <Table size="small" stickyHeader style={{ minWidth: 720, width: "100%" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: 40 }} />
                                <TableCell style={{ minWidth: 160 }}>Nome</TableCell>
                                <TableCell style={{ minWidth: 160, maxWidth: 160 }}>E-mail</TableCell>
                                <TableCell style={{ minWidth: 120 }}>Telefone</TableCell>
                                <TableCell style={{ minWidth: 100 }}>Cadastro</TableCell>
                                <TableCell style={{ minWidth: 100 }}>Nascimento</TableCell>
                                <TableCell style={{ minWidth: 70 }} align="center">Selos</TableCell>
                                <TableCell style={{ width: 48 }} align="center" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dados.clientes.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className={classes.semResultado}>
                                        A pesquisa não retornou resultados.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                dados.clientes.map(cliente => (
                                    <TableRow key={cliente.id} hover>
                                        <TableCell>
                                            <Avatar
                                                src={
                                                    cliente.facebookId
                                                        ? `https://graph.facebook.com/${cliente.facebookId}/picture?type=square`
                                                        : "https://s3-sa-east-1.amazonaws.com/clientefielsp/aplicacao/semfoto.png"
                                                }
                                                style={{ width: 32, height: 32 }}
                                            />
                                        </TableCell>
                                        <TableCell
                                            className={classes.nomeCell}
                                            title={cliente.nome}
                                            onClick={() => history.push(`/clientes/${cliente.id}`)}
                                        >
                                            {cliente.nome}
                                        </TableCell>
                                        <TableCell
                                            className={classes.emailCell}
                                            title={cliente.email || ""}
                                        >
                                            {cliente.email || "-"}
                                        </TableCell>
                                        <TableCell>{cliente.telefone || "-"}</TableCell>
                                        <TableCell>{formatarData(cliente.dataCadastro)}</TableCell>
                                        <TableCell>{formatarData(cliente.dataNascimento)}</TableCell>
                                        <TableCell align="center">{cliente.totalSelos ?? "-"}</TableCell>
                                        <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                                            {cliente.telefone && (
                                                <Tooltip title="Enviar WhatsApp">
                                                    <IconButton
                                                        size="small"
                                                        className={classes.wppBtn}
                                                        component="a"
                                                        href={linkWhatsapp(cliente)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <WhatsApp fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    count={dados.total}
                    page={dados.pagina}
                    rowsPerPage={dados.tamanhoPagina}
                    rowsPerPageOptions={[dados.tamanhoPagina]}
                    onPageChange={handleMudarPagina}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
                    }
                />
            </Box>
        </Paper>
    )
}
